// src/features/invite/AcceptSuperAdminInvite.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  CheckCircle, 
  XCircle, 
  Eye, 
  EyeOff, 
  AlertTriangle,
  Building2,
  User,
  Phone,
  Lock,
  Shield,
  Sparkles,
  UserPlus
} from 'lucide-react';
import { sbaService } from '../../services';
import SetupSimulation from './SetupSimulation';
import './AcceptSuperAdminInvite.css';

const AcceptSuperAdminInvite = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get token from URL query params
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(true);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [alreadyAccepted, setAlreadyAccepted] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  
  // Form fields
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');
  
  // UI states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState({
    fullName: false,
    password: false,
    confirmPassword: false
  });

  // Password requirements
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    number: false,
    special: false
  });

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenValid(false);
        setError('No invite token provided. Please check your invitation link.');
        setVerifying(false);
        setLoading(false);
        return;
      }

      setVerifying(true);
      setLoading(true);
      
      try {
        const result = await sbaService.verifyInvite(token);
        
        if (result.success && result.data?.valid) {
          setTokenValid(true);
          setInviteData(result.data);
          setCompanyName(result.data.company_name || '');
          setFullName(result.data.full_name || '');
        } else {
          // ✅ Use status codes for better error detection
          const msg = result.message || 'Invalid or expired invite link';
          const status = result.status;
          const lowerMsg = msg.toLowerCase();

          if (status === 400 && lowerMsg.includes('expired')) {
            setTokenExpired(true);
          } else if (status === 400 && (lowerMsg.includes('already') || lowerMsg.includes('accepted'))) {
            setAlreadyAccepted(true);
          } else if (status === 403) {
            // Subdomain mismatch or company suspended
            setTokenValid(false);
          } else if (status === 404) {
            // Company not found
            setTokenValid(false);
          } else if (status === 500) {
            // Config error
            setTokenValid(false);
          } else {
            setTokenValid(false);
          }
          setError(msg);
        }
      } catch (err) {
        setTokenValid(false);
        setError(err.message || 'Failed to verify invitation. Please try again.');
      }
      
      setVerifying(false);
      setLoading(false);
    };
    
    verifyToken();
  }, [token]);

  // Password validation
  useEffect(() => {
    setPasswordChecks({
      length: password.length >= 8,
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!fullName.trim() || !password || !confirmPassword) {
      setTouched({
        fullName: true,
        password: true,
        confirmPassword: true
      });
      setError('Please fill in all required fields.');
      return;
    }
    
    // Validate password match
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    
    // Validate password strength
    if (!passwordChecks.length || !passwordChecks.number || !passwordChecks.special) {
      setError('Password must meet all requirements.');
      return;
    }
    
    setSubmitting(true);
    setError('');

    try {
      const result = await sbaService.acceptInvite(
        token,
        password,
        confirmPassword,
        fullName,
        phone
      );

      if (result.success) {
        // Show setup simulation
        setShowSetup(true);
      } else {
        setError(result.message || 'Failed to accept invitation. Please try again.');
        setSubmitting(false);
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred. Please try again.');
      setSubmitting(false);
    }
  };

  const handleSetupComplete = () => {
    setShowSetup(false);
    setSubmitting(false);
    setSuccess(true);
  };

  const allRequirementsMet = passwordChecks.length && passwordChecks.number && passwordChecks.special;
  const isFormValid = fullName.trim() && password && confirmPassword && allRequirementsMet && password === confirmPassword;

  // Loading state - Verifying
  if (verifying) {
    return (
      <div className="accept-superadmin-page">
        <div className="accept-superadmin-loading">
          <div className="accept-superadmin-spinner"></div>
          <p>Verifying invitation...</p>
        </div>
      </div>
    );
  }

  // Invalid token
  if (!tokenValid || tokenExpired || alreadyAccepted) {
    return (
      <div className="accept-superadmin-page">
        <div className="accept-superadmin-card accept-superadmin-error-card">
          <div className="accept-superadmin-error-icon">
            <XCircle size={48} />
          </div>
          <h2 className="accept-superadmin-error-title">
            {tokenExpired ? 'Invitation Expired' : alreadyAccepted ? 'Already Accepted' : 'Invalid Invitation'}
          </h2>
          <p className="accept-superadmin-error-message">{error}</p>
          <button 
            className="accept-superadmin-error-btn"
            onClick={() => navigate('/')}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="accept-superadmin-page">
        <div className="accept-superadmin-card accept-superadmin-success-card">
          <div className="accept-superadmin-success-icon">
            <CheckCircle size={48} />
          </div>
          <h2 className="accept-superadmin-success-title">Account Activated!</h2>
          <p className="accept-superadmin-success-message">
            Your account has been created successfully.
            <br />
            You can now login to manage <strong>{companyName}</strong>.
          </p>
          <button 
            className="accept-superadmin-success-btn"
            onClick={() => navigate('/')}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-superadmin-page">
      {/* Setup Simulation Overlay */}
      {showSetup && (
        <SetupSimulation onComplete={handleSetupComplete} />
      )}

      <div className="accept-superadmin-container">
        <div className="accept-superadmin-card">
          <div className="accept-superadmin-body">
            {/* Header */}
            <div className="accept-superadmin-header">
              <div className="accept-superadmin-icon">
                <UserPlus size={28} color="white" />
              </div>
              <div>
                <h1 className="accept-superadmin-title">Accept Invitation</h1>
                <p className="accept-superadmin-subtitle">
                  Complete your account setup
                </p>
              </div>
            </div>

            {/* Invite Details */}
            <div className="accept-superadmin-info-box">
              <div className="accept-superadmin-info-item">
                <Building2 size={16} />
                <span>{companyName || 'Organization'}</span>
              </div>
              {inviteData?.email && (
                <div className="accept-superadmin-info-item">
                  <User size={16} />
                  <span>{inviteData.email}</span>
                </div>
              )}
            </div>

            {/* Form */}
            <form className="accept-superadmin-form" onSubmit={handleSubmit}>
              {/* Full Name */}
              <div className="accept-superadmin-form-group">
                <label className="accept-superadmin-label">
                  Full Name <span className="accept-superadmin-required">*</span>
                </label>
                <div className="accept-superadmin-input-wrapper">
                  <User size={18} className="accept-superadmin-input-icon" />
                  <input
                    type="text"
                    className={`accept-superadmin-input ${touched.fullName && !fullName.trim() ? 'accept-superadmin-input-error' : ''}`}
                    placeholder="Enter your full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    onBlur={() => setTouched({ ...touched, fullName: true })}
                    disabled={submitting}
                    required
                  />
                </div>
                {touched.fullName && !fullName.trim() && (
                  <span className="accept-superadmin-error-text">Full name is required</span>
                )}
              </div>

              {/* Phone */}
              <div className="accept-superadmin-form-group">
                <label className="accept-superadmin-label">
                  Phone Number
                </label>
                <div className="accept-superadmin-input-wrapper">
                  <Phone size={18} className="accept-superadmin-input-icon" />
                  <input
                    type="tel"
                    className="accept-superadmin-input"
                    placeholder="+254 712 345 678"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={submitting}
                  />
                </div>
                <span className="accept-superadmin-hint">Optional but recommended</span>
              </div>

              {/* Password */}
              <div className="accept-superadmin-form-group">
                <label className="accept-superadmin-label">
                  Password <span className="accept-superadmin-required">*</span>
                </label>
                <div className="accept-superadmin-input-wrapper">
                  <Lock size={18} className="accept-superadmin-input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={`accept-superadmin-input ${touched.password && !password ? 'accept-superadmin-input-error' : ''}`}
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => setTouched({ ...touched, password: true })}
                    disabled={submitting}
                    required
                  />
                  <button
                    type="button"
                    className="accept-superadmin-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              {password && (
                <div className="accept-superadmin-password-requirements">
                  <div className={`accept-superadmin-requirement ${passwordChecks.length ? 'accept-superadmin-requirement-met' : ''}`}>
                    {passwordChecks.length ? <CheckCircle size={14} /> : <XCircle size={14} />}
                    At least 8 characters
                  </div>
                  <div className={`accept-superadmin-requirement ${passwordChecks.number ? 'accept-superadmin-requirement-met' : ''}`}>
                    {passwordChecks.number ? <CheckCircle size={14} /> : <XCircle size={14} />}
                    Contains a number
                  </div>
                  <div className={`accept-superadmin-requirement ${passwordChecks.special ? 'accept-superadmin-requirement-met' : ''}`}>
                    {passwordChecks.special ? <CheckCircle size={14} /> : <XCircle size={14} />}
                    Contains a special character
                  </div>
                </div>
              )}

              {/* Confirm Password */}
              <div className="accept-superadmin-form-group">
                <label className="accept-superadmin-label">
                  Confirm Password <span className="accept-superadmin-required">*</span>
                </label>
                <div className="accept-superadmin-input-wrapper">
                  <Lock size={18} className="accept-superadmin-input-icon" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={`accept-superadmin-input ${touched.confirmPassword && confirmPassword && password !== confirmPassword ? 'accept-superadmin-input-error' : ''}`}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onBlur={() => setTouched({ ...touched, confirmPassword: true })}
                    disabled={submitting}
                    required
                  />
                  <button
                    type="button"
                    className="accept-superadmin-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex="-1"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {touched.confirmPassword && confirmPassword && password !== confirmPassword && (
                  <span className="accept-superadmin-error-text">Passwords do not match</span>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="accept-superadmin-form-error">
                  <AlertTriangle size={16} />
                  <span>{error}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="accept-superadmin-submit-btn"
                disabled={!isFormValid || submitting}
              >
                {submitting ? (
                  <>
                    <span className="accept-superadmin-spinner-small"></span>
                    Creating your account...
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    Accept Invitation
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="accept-superadmin-footer">
              <span>
                <Shield size={14} />
                Secure connection
              </span>
              <span>
                <Sparkles size={14} />
                Welcome aboard
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptSuperAdminInvite;